import { keyframes } from "@emotion/react";
import { TFunction } from "i18next";
import { CheckCircleIcon, XIcon } from "lucide-react";
import { ComponentPropsWithoutRef, Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { QrReader } from "react-qr-reader";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import tw from "twin.macro";

import { useAreas, useDoesModuleAlreadyExists } from "@/api/areas/queries";
import { Area } from "@/api/areas/types";
import { useClients } from "@/api/clients/queries";
import { Client } from "@/api/clients/types";
import Field from "@/components/field";
import { LoadingSpinner } from "@/components/LoadingIndicator";
import { PageContainer, PageSubTitle, PageTitle } from "@/components/page";
import Select from "@/components/selector";
import { usePersistedState } from "@/hooks/usePersistedState";

const showFailureToast = (
  translationKey: string,
  translator: TFunction<"translation", undefined>
) => {
  toast.custom(
    (toastId) => (
      /* Désolé pour ce hack pas très jojo*/
      <div tw="relative w-full h-full">
        <div
          role="status"
          tw="flex items-start justify-center gap-3 rounded-lg bg-red-500 px-4 py-3 shadow-sm min-w-[280px] max-w-[360px] absolute top-0 right-0 min-[604px]:static"
        >
          <div tw="flex shrink-0 bg-white items-center justify-center rounded-full w-8 h-8 mt-3">
            <XIcon tw="w-5 h-5 text-red-500" />
          </div>
          <div tw="flex flex-1 flex-col gap-1 min-w-0">
            <p tw="text-lg font-bold text-white">
              {translator("quickPost.error")}
            </p>
            <p tw="text-base font-medium text-white">
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              {translator(translationKey as any)}
            </p>
          </div>
          <button
            type="button"
            aria-label={translator("global.close")}
            onClick={() => toast.dismiss(toastId)}
            tw="shrink-0 rounded p-1 text-white hover:bg-red-600 focus:(outline-none ring-2 ring-offset-1 ring-red-600)"
          >
            <XIcon tw="w-4 h-4" />
          </button>
        </div>
      </div>
    ),
    { duration: Infinity, id: "quick-post-module-failure" }
  );
};

const greenBorderFade = keyframes({
  from: { borderColor: "rgb(34, 197, 94)" },
  to: { borderColor: "transparent" },
});

interface ScanDataValues {
  name: string;
  address: number;
}

const QuickModuleAccess = () => {
  const { t } = useTranslation();
  const { data: clients } = useClients();
  const { data: areas } = useAreas();

  const [scanData, setScanData] = useState<ScanDataValues>({
    name: "",
    address: 0,
  });

  const [selectedClient, setSelectedClient] = usePersistedState<Client | null>(
    "quickaccess:selectedClient",
    null
  );
  const [selectedArea, setSelectedArea] = usePersistedState<Area | null>(
    "quickaccess:selectedArea",
    null
  );
  const [areaArray, setAreaArray] = usePersistedState<Area[]>(
    "quickaccess:areaArray",
    []
  );
  const [showGreenBorder, setShowGreenBorder] = useState(false);
  const [showCheckCircle, setShowCheckCircle] = useState(false);
  const [
    shouldDisplayAccessToModuleDetails,
    setShouldDisplayAccessToModuleDetails,
  ] = useState(false);
  const [moduleAddress, setModuleAddress] = useState<number>(0);

  const { data: doesModuleAlreadyExists, isError } = useDoesModuleAlreadyExists(
    {
      areaId: selectedArea?._id,
      moduleAddress: scanData.address?.toString(),
    }
  );
  const navigate = useNavigate();

  // Invalide le client persisté s'il n'existe plus côté serveur
  useEffect(() => {
    if (!clients || !selectedClient) return;
    const stillExists = clients.some((c) => c._id === selectedClient._id);
    if (!stillExists) setSelectedClient(null);
  }, [clients]);

  // Invalide l'area persistée si elle n'existe plus côté serveur
  useEffect(() => {
    if (!areas || !selectedArea) return;
    const stillExists = areas.some((a) => a._id === selectedArea._id);
    if (!stillExists) setSelectedArea(null);
  }, [areas]);

  const handleSelectClient = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const client = clients?.find((client) => client._id === e.target.value);
    setSelectedClient(client || null);

    if (client) {
      setAreaArray(
        // @ts-expect-error - areas is not defined
        areas ? areas.filter((area) => client.areas?.includes(area._id)) : []
      );
    } else {
      setAreaArray([]);
      setSelectedArea(null);
    }
  };

  const handleSelectArea = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedArea(
      areaArray.find((area) => area._id === e.target.value) || null
    );
  };

  useEffect(() => {
    if (isError) {
      showFailureToast("quickModuleAccess.errorDuringAccess", t);
      return;
    }

    if (false === doesModuleAlreadyExists) {
      showFailureToast("quickModuleAccess.moduleDoesNotExist", t);
      return;
    }

    if (doesModuleAlreadyExists) {
      toast
        .getToasts()
        .forEach((toastElement) => toast.dismiss(toastElement.id));

      setTimeout(() => {
        setShouldDisplayAccessToModuleDetails(true);
        setModuleAddress(scanData.address);
      }, 1000);

      setTimeout(() => {
        navigate(
          `/clients/${selectedClient!._id}/areas/${selectedArea!._id}/post/${scanData.address}`
        );
      }, 3500);
      return;
    }
  }, [doesModuleAlreadyExists, isError]);

  return (
    <PageContainer>
      <div tw="w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div tw="max-w-7xl flex flex-col gap-8">
          <div tw="flex flex-col gap-4">
            <PageTitle>{t("quickModuleAccess.title")}</PageTitle>
            <PageSubTitle>{t("quickModuleAccess.subtitle")}</PageSubTitle>
          </div>
          <Field tw="flex-1 sm:w-64">
            <Select
              onChange={handleSelectClient}
              value={selectedClient?._id ?? ""}
            >
              <option value="">{t("form.selectCustomer")}</option>
              {clients && clients.length > 0 && (
                <Fragment>
                  {clients.map((client: Client) => (
                    <option key={client._id} value={client._id}>
                      {client.name}
                    </option>
                  ))}
                </Fragment>
              )}
            </Select>
          </Field>
          <Field
            tw="flex-1 sm:w-64 transition-opacity duration-1000 ease-out opacity-0"
            css={[selectedClient && tw`opacity-100`]}
          >
            <Select onChange={handleSelectArea} value={selectedArea?._id ?? ""}>
              <option value="">{t("form.selectArea")}</option>
              <Fragment>
                {areaArray.map((area: Area) => (
                  <option key={area._id} value={area._id}>
                    {area.name}
                  </option>
                ))}
              </Fragment>
            </Select>
          </Field>

          <div
            tw="max-w-xl flex flex-col gap-8 transition-opacity duration-1000 ease-out opacity-0"
            css={[selectedArea && tw`opacity-100 delay-200`]}
          >
            <div tw="isolate relative rounded-3xl overflow-hidden bg-black">
              {showCheckCircle && (
                <div tw="absolute inset-0 flex items-center justify-center z-10">
                  <CheckCircleIcon tw="w-10 h-10 text-green-500" />
                </div>
              )}
              <QrReader
                constraints={{
                  aspectRatio: { ideal: 1 },
                  facingMode: "environment",
                }}
                videoId="qrScanner"
                ViewFinder={(props) => (
                  <Mask {...props} showGreenBorder={showGreenBorder} />
                )}
                scanDelay={500}
                onResult={async (result) => {
                  if (!result) {
                    return;
                  }
                  const text = result?.getText();

                  if (text !== undefined && text !== null) {
                    const lastDigitsMatch = text.match(/(\d+)(?!.*\d)/);
                    if (!lastDigitsMatch) return;

                    setScanData({
                      name: scanData.name,
                      address: Number(lastDigitsMatch[1]),
                    });
                    setShowGreenBorder(true);
                    setShowCheckCircle(true);

                    return;
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>
      {shouldDisplayAccessToModuleDetails && (
        <AccessToModuleDetails moduleAddress={moduleAddress} />
      )}
    </PageContainer>
  );
};

export default QuickModuleAccess;

type MaskProps = ComponentPropsWithoutRef<"svg"> & {
  color?: string;
  showGreenBorder?: boolean;
};

const Mask = ({
  color = "currentColor",
  showGreenBorder,
  ...props
}: MaskProps) => (
  <svg
    viewBox="0 0 100 100"
    tw="absolute inset-0 z-10 border-black/40 border-[2rem] text-primary-200"
    css={[
      showGreenBorder && {
        borderColor: "rgb(34, 197, 94)",
        animationName: greenBorderFade,
        animationDuration: "8s",
        animationTimingFunction: "ease-out",
        animationFillMode: "forwards",
      },
    ]}
    {...props}
  >
    <path fill="none" d="M13,0 L0,0 L0,13" stroke={color} strokeWidth="5" />
    <path fill="none" d="M0,87 L0,100 L13,100" stroke={color} strokeWidth="5" />
    <path
      fill="none"
      d="M87,100 L100,100 L100,87"
      stroke={color}
      strokeWidth="5"
    />
    <path fill="none" d="M100,13 L100,0 87,0" stroke={color} strokeWidth="5" />
  </svg>
);

const AccessToModuleDetails = ({
  moduleAddress,
}: {
  moduleAddress: number;
}) => {
  const { t } = useTranslation();
  return (
    <div tw="flex justify-center items-center h-screen gap-2 absolute bottom-0 left-0 right-0 bg-white">
      <div tw="absolute top-1/4 left-0 right-0">
        <div tw="flex justify-center items-center gap-2">
          <LoadingSpinner tw="text-primary-800 size-20" />
          <p tw="p-2 text-primary-800 text-sm">
            {t("quickModuleAccess.accessingData1")}{" "}
            <span tw="font-bold">{moduleAddress}</span>{" "}
            {t("quickModuleAccess.accessingData2")}
          </p>
        </div>
      </div>
    </div>
  );
};
